import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdminAuth, type Stage, type AdminProgress } from "@/hooks/useAdminAuth";
import { deleteFileByUrl, resolveUploadUrl } from "@/lib/fileStorage";
import { SERVICE_PLAN_CONFIG, type ExpenseEntry, parseExpenseData } from "@/pages/admin/progress/constants";
import StageList from "@/pages/admin/progress/components/StageList";
import StageEditor from "@/pages/admin/progress/components/StageEditor";
import NotifyModal from "@/pages/admin/progress/components/NotifyModal";

interface ClientInfo {
  id: number; name: string | null; phone: string;
  service_plan?: string | null; is_single?: boolean | null;
  partner_name?: string | null; joint_stimulation?: boolean | null;
}

function compressImageToBlob(file: File, maxWidth = 1200, quality = 0.75): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let w = img.width, h = img.height;
      if (w > maxWidth) { h = Math.round((h * maxWidth) / w); w = maxWidth; }
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => { URL.revokeObjectURL(img.src); if (blob) resolve(blob); else reject(new Error("图片压缩失败")); }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error("图片加载失败")); };
    img.src = URL.createObjectURL(file);
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        const base64 = reader.result.split(",")[1] || reader.result;
        resolve(base64);
      } else {
        reject(new Error("读取文件失败"));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function AdminProgressPage() {
  const { fetchClients, fetchClientDetail, fetchAllStages, updateProgress, sendNotification, hasProgressSubPermission, uploadFile } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [progress, setProgress] = useState<AdminProgress[]>([]);
  const [selectedClient, setSelectedClient] = useState<number | null>(Number(searchParams.get("client_id")) || null);
  const [selectedStage, setSelectedStage] = useState<number | null>(Number(searchParams.get("stage_id")) || null);
  const [clientServicePlan, setClientServicePlan] = useState("surrogacy_has_embryo");
  const [clientJointStim, setClientJointStim] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [toastFading, setToastFading] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    setMessage(msg);
    setToastFading(false);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToastFading(true);
      toastTimerRef.current = setTimeout(() => {
        setToastVisible(false);
        setMessage("");
        setToastFading(false);
      }, 400);
    }, 2000);
  }, []);

  const [status, setStatus] = useState("pending");
  const [notes, setNotes] = useState("");
  const [feeReceivable, setFeeReceivable] = useState("");
  const [feeReceived, setFeeReceived] = useState("");
  const [feeUsdReceivable, setFeeUsdReceivable] = useState("");
  const [feeUsdReceived, setFeeUsdReceived] = useState("");
  const [media, setMedia] = useState<Array<{ url: string; type: string; title: string }>>([]);
  const [documents, setDocuments] = useState<Array<{ url: string; type: string; title: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState<Array<{fileName: string; percent: number}>>([]);
  const [docUploadProgress, setDocUploadProgress] = useState<Array<{fileName: string; percent: number}>>([]);

  const [expenseData, setExpenseData] = useState<Record<string, ExpenseEntry>>();
  const [activeExpenseTypes, setActiveExpenseTypes] = useState<Set<string>>(new Set());

  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string; title: string } | null>(null);

  const hasExpensePerm = hasProgressSubPermission("expense_management");

  useEffect(() => {
    const load = async () => {
      const [clientsRes, stagesRes] = await Promise.all([fetchClients({ limit: 100 }), fetchAllStages()]);
      if (clientsRes?.success) setClients(clientsRes.clients || []);
      if (stagesRes?.success) setStages(stagesRes.stages || []);
      setLoading(false);
    };
    load();
  }, [fetchClients, fetchAllStages]);

  useEffect(() => {
    if (!selectedClient) return;
    const load = async () => {
      setLoading(true);
      const res = await fetchClientDetail(selectedClient);
      setLoading(false);
      if (res?.success) {
        const progressData: AdminProgress[] = (res.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] }));
        setProgress(progressData);
        const plan = res.client?.service_plan || "surrogacy_has_embryo";
        setClientServicePlan(plan);
        setClientJointStim(res.client?.joint_stimulation === true);
        const config = SERVICE_PLAN_CONFIG[plan] || SERVICE_PLAN_CONFIG.surrogacy_has_embryo;
        const clientProgress = progressData.filter((p) => p.stage_id >= config.range[0] && p.stage_id <= config.range[1]);
        const sortedClientP = [...clientProgress].sort((a, b) => a.stage_id - b.stage_id);
        if (sortedClientP.length > 0 && sortedClientP[0].status === "pending") {
          await updateProgress({ client_id: selectedClient, stage_id: sortedClientP[0].stage_id, status: "in_progress", notes: sortedClientP[0].notes || "", start_now: true, complete_now: false } as any);
          const refreshed = await fetchClientDetail(selectedClient);
          if (refreshed?.success) { const rp = (refreshed.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] })); setProgress(rp); }
        }
        const urlStageId = Number(searchParams.get("stage_id")) || null;
        if (urlStageId && urlStageId >= config.range[0] && urlStageId <= config.range[1]) setSelectedStage(urlStageId);
        else if (clientProgress.length > 0) setSelectedStage(clientProgress[0].stage_id);
        else setSelectedStage(null);
      }
    };
    load();
  }, [selectedClient, fetchClientDetail, searchParams, updateProgress]);

  useEffect(() => {
    if (!selectedStage) return;
    const p = progress.find((x) => x.stage_id === selectedStage);
    if (p) {
      setStatus(p.status || "pending"); setNotes(p.notes || "");
      setFeeReceivable(p.fee_receivable != null ? String(p.fee_receivable) : "");
      setFeeReceived(p.fee_received != null ? String(p.fee_received) : "");
      setFeeUsdReceivable((p as any).fee_usd_receivable != null ? String((p as any).fee_usd_receivable) : "");
      setFeeUsdReceived((p as any).fee_usd_received != null ? String((p as any).fee_usd_received) : "");
      setMedia(Array.isArray(p.media) ? p.media : []);
      setDocuments(Array.isArray(p.documents) ? p.documents : []);
      const ed = parseExpenseData((p as any).expense_data);
      setExpenseData(ed);
      const active = new Set<string>();
      for (const t of [{ key: "employee_commission" }, { key: "channel_commission" }, { key: "other" }]) {
        const e = ed[t.key]; if (e && (e.receivable > 0 || e.received > 0 || e.usd_receivable > 0 || e.usd_received > 0)) active.add(t.key);
      }
      setActiveExpenseTypes(active);
    } else {
      setStatus("pending"); setNotes(""); setFeeReceivable(""); setFeeReceived(""); setFeeUsdReceivable(""); setFeeUsdReceived(""); setMedia([]); setDocuments([]); setExpenseData({}); setActiveExpenseTypes(new Set());
    }
  }, [selectedStage, progress]);

  const handleSendNotification = async (title: string, content: string) => {
    if (!selectedClient) return;
    const res = await sendNotification({ client_id: selectedClient, title, content, type: "system" });
    if (res?.success) { showToast("通知发送成功！"); }
    else showToast(res?.error || "通知发送失败");
  };

  const handleUpload = useCallback(async (files: FileList | null, bucket: string) => {
    if (!files || files.length === 0) return;
    if (!selectedClient || !selectedStage) { showToast("请先选择客户和阶段"); return; }
    const fileList = Array.from(files);
    if (fileList.length > 5) { showToast("单次最多上传 5 个文件"); return; }
    const maxMb = bucket === "crm-media" ? 20 : 10;
    const oversized = fileList.filter((f) => f.size > maxMb * 1024 * 1024);
    if (oversized.length > 0) { showToast(`文件过大：${oversized.map((f) => `${f.name}(${(f.size / 1024 / 1024).toFixed(1)}MB)`).join("、")}，单个文件不超过${maxMb}MB`); return; }

    setUploading(true);
    const isMediaUpload = bucket === "crm-media";
    const initProgress = fileList.map(f => ({ fileName: f.name, percent: 0 }));
    if (isMediaUpload) { setMediaUploadProgress(initProgress); setDocUploadProgress([]); }
    else { setDocUploadProgress(initProgress); setMediaUploadProgress([]); }

    let hasError = false;
    let newMedia = [...media];
    let newDocs = [...documents];
    const uploadedFileNames: string[] = [];

    try {
      for (let idx = 0; idx < fileList.length; idx++) {
        const file = fileList[idx];
        const updateFilePct = (pct: number) => {
          if (isMediaUpload) setMediaUploadProgress(prev => prev.map(p => p.fileName === file.name ? { ...p, percent: pct } : p));
          else setDocUploadProgress(prev => prev.map(p => p.fileName === file.name ? { ...p, percent: pct } : p));
        };
        const removeFileBar = () => {
          if (isMediaUpload) setMediaUploadProgress(prev => prev.filter(p => p.fileName !== file.name));
          else setDocUploadProgress(prev => prev.filter(p => p.fileName !== file.name));
        };
        updateFilePct(5);
        try {
          let uploadBlob: File | Blob = file;
          if (isMediaUpload && file.type.startsWith("image/")) uploadBlob = await compressImageToBlob(file, 1200, 0.75);
          updateFilePct(25);

          // base64 编码后约为原始大小的 4/3，根据 bucket 限制做二次保护
          const maxBase64Mb = bucket === "crm-media" ? 28 : 14;
          const estimatedBase64Size = (uploadBlob.size * 4) / 3;
          if (estimatedBase64Size > maxBase64Mb * 1024 * 1024) {
            hasError = true;
            showToast(`上传失败: ${file.name} — 文件过大（编码后约${(estimatedBase64Size / 1024 / 1024).toFixed(1)}MB，超过传输限制），请尝试压缩后重新上传`);
            removeFileBar();
            continue;
          }

          const fileType = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : file.type.includes("pdf") ? "pdf" : "doc";
          updateFilePct(50);
          const base64 = await blobToBase64(uploadBlob);
          updateFilePct(75);
          const result = await uploadFile({ file_base64: base64, file_name: file.name, bucket, content_type: file.type || "application/octet-stream", client_id: selectedClient, stage_id: selectedStage, stage_name: stages.find((s) => s.id === selectedStage)?.name || "" });
          if (!result?.success) {
            hasError = true;
            showToast(`上传失败: ${file.name} — ${result?.error || "未知错误"}`);
            removeFileBar();
            continue;
          }
          const fileRecord = { url: result.url, type: fileType, title: file.name };
          if (bucket === "crm-media") newMedia = [...newMedia, fileRecord];
          else newDocs = [...newDocs, fileRecord];
          uploadedFileNames.push(file.name);
          updateFilePct(100);
          removeFileBar();
        } catch (e: any) {
          hasError = true;
          const errMsg = e.message || "未知错误";
          if (errMsg.includes("过大") || errMsg.includes("size") || errMsg.includes("limit")) {
            showToast(`上传失败: ${file.name} — 文件过大，请尝试压缩后重新上传`);
          } else if (errMsg.includes("timeout") || errMsg.includes("超时")) {
            showToast(`上传失败: ${file.name} — 网络超时，请检查网络后重试`);
          } else {
            showToast(`处理失败: ${file.name} — ${errMsg}`);
          }
          removeFileBar();
        }
      }

      if (!hasError && (newMedia.length !== media.length || newDocs.length !== documents.length)) {
        const savePayload: Record<string, unknown> = { client_id: selectedClient, stage_id: selectedStage };
        if (bucket === "crm-media") savePayload.media = newMedia;
        else savePayload.documents = newDocs;
        savePayload.file_operations = bucket === "crm-media" ? { upload_media: uploadedFileNames } : { upload_documents: uploadedFileNames };
        const saveRes = await updateProgress(savePayload as any);
        if (saveRes?.success) { if (bucket === "crm-media") setMedia(newMedia); else setDocuments(newDocs); }
        else { hasError = true; showToast(`保存记录失败: ${saveRes?.error || "未知错误"}`); }
      }

      if (!hasError) {
        showToast(isMediaUpload ? "影像已自动保存" : "文档已自动保存");
        if (selectedClient) {
          const detail = await fetchClientDetail(selectedClient);
          if (detail?.success) { const rp = (detail.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] })); setProgress(rp); }
        }
      }
    } catch (outerErr: any) {
      hasError = true;
      showToast(`上传过程异常: ${outerErr.message || "未知错误"}`);
    } finally {
      setUploading(false);
      if (isMediaUpload) setMediaUploadProgress([]);
      else setDocUploadProgress([]);
    }
  }, [selectedClient, selectedStage, media, documents, stages, updateProgress, fetchClientDetail, uploadFile, showToast]);

  const handleSave = async () => {
    if (!selectedClient || !selectedStage) return;
    setSaving(true);
    const currentProgress = progress.find((x) => x.stage_id === selectedStage);
    const currentStage = stages.find((s) => s.id === selectedStage);
    const payload: Record<string, unknown> = { client_id: selectedClient, stage_id: selectedStage, status, notes, start_now: !currentProgress?.started_at && status === "in_progress", complete_now: !currentProgress?.completed_at && status === "completed" };
    if (currentStage?.is_income) {
      const receivable = feeReceivable ? parseFloat(feeReceivable) : 0;
      payload.fee_receivable = receivable;
      payload.fee_received = Math.min(feeReceived ? parseFloat(feeReceived) : 0, receivable);
      const usdReceivable = feeUsdReceivable ? parseFloat(feeUsdReceivable) : 0;
      payload.fee_usd_receivable = usdReceivable;
      payload.fee_usd_received = Math.min(feeUsdReceived ? parseFloat(feeUsdReceived) : 0, usdReceivable);
    }
    const isExpense = currentStage?.is_expense === true;
    if (isExpense) {
      const expPayload: Record<string, any> = {};
      for (const t of [{ key: "employee_commission" }, { key: "channel_commission" }, { key: "other" }]) {
        if (activeExpenseTypes.has(t.key)) {
          const e = expenseData[t.key] || { receivable: 0, received: 0, usd_receivable: 0, usd_received: 0 };
          expPayload[t.key] = { receivable: parseFloat(String(e.receivable || 0)), received: Math.min(parseFloat(String(e.received || 0)), parseFloat(String(e.receivable || 0))), usd_receivable: parseFloat(String(e.usd_receivable || 0)), usd_received: Math.min(parseFloat(String(e.usd_received || 0)), parseFloat(String(e.usd_receivable || 0))) };
        } else { expPayload[t.key] = { receivable: 0, received: 0, usd_receivable: 0, usd_received: 0 }; }
      }
      payload.expense_data = expPayload;
    }
    const res = await updateProgress(payload as any);
    setSaving(false);
    if (res?.success) {
      showToast("保存成功！");
      const detail = await fetchClientDetail(selectedClient);
      if (detail?.success) {
        const updated = (detail.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] }));
        setProgress(updated);
        if (status === "completed") {
          const cfg = SERVICE_PLAN_CONFIG[clientServicePlan] || SERVICE_PLAN_CONFIG.surrogacy_has_embryo;
          const sortedStages = [...stages].filter((s) => s.id >= cfg.range[0] && s.id <= cfg.range[1]).sort((a, b) => a.sort_order - b.sort_order);
          const curIdx = sortedStages.findIndex((s) => s.id === selectedStage);
          if (curIdx >= 0 && curIdx < sortedStages.length - 1) {
            const nextStage = sortedStages[curIdx + 1];
            const nextP = updated.find((p: any) => p.stage_id === nextStage.id);
            if (nextP && nextP.status === "pending") {
              await updateProgress({ client_id: selectedClient, stage_id: nextStage.id, status: "in_progress", notes: nextP.notes || "", start_now: true, complete_now: false } as any);
              const detail2 = await fetchClientDetail(selectedClient);
              if (detail2?.success) { const r2 = (detail2.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] })); setProgress(r2); }
            }
          }
        }
      }
    } else showToast(res?.error || "保存失败");
  };

  const removeMedia = async (index: number) => {
    if (!selectedClient || !selectedStage) return;
    const file = media[index];
    const newMedia = media.filter((_, i) => i !== index);
    if (file.url?.startsWith("http")) { try { await deleteFileByUrl(file.url, "crm-media"); } catch { /* ignore */ } }
    const res = await updateProgress({ client_id: selectedClient, stage_id: selectedStage, media: newMedia, file_operations: { delete_media: file.title } });
    if (res?.success) { setMedia(newMedia); showToast("影像已确认删除"); const detail = await fetchClientDetail(selectedClient); if (detail?.success) { const r = (detail.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] })); setProgress(r); } }
    else showToast(res?.error || "删除失败");
  };

  const removeDoc = async (index: number) => {
    if (!selectedClient || !selectedStage) return;
    const file = documents[index];
    const newDocs = documents.filter((_, i) => i !== index);
    if (file.url?.startsWith("http")) { try { await deleteFileByUrl(file.url, "crm-documents"); } catch { /* ignore */ } }
    const res = await updateProgress({ client_id: selectedClient, stage_id: selectedStage, documents: newDocs, file_operations: { delete_documents: file.title } });
    if (res?.success) { setDocuments(newDocs); showToast("文档已确认删除"); const detail = await fetchClientDetail(selectedClient); if (detail?.success) { const r = (detail.progress || []).map((p: any) => ({ ...p, media: Array.isArray(p.media) ? p.media : [], documents: Array.isArray(p.documents) ? p.documents : [] })); setProgress(r); } }
    else showToast(res?.error || "删除失败");
  };

  const handleReset = () => {
    const p = progress.find((x) => x.stage_id === selectedStage);
    if (p) { setStatus(p.status || "pending"); setNotes(p.notes || ""); setFeeReceivable(p.fee_receivable != null ? String(p.fee_receivable) : ""); setFeeReceived(p.fee_received != null ? String(p.fee_received) : ""); setMedia(Array.isArray(p.media) ? p.media : []); setDocuments(Array.isArray(p.documents) ? p.documents : []); }
  };

  const toggleExpenseType = (key: string) => {
    setActiveExpenseTypes((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const updateExpenseField = (typeKey: string, field: string, value: string) => {
    setExpenseData((prev) => {
      const current = prev[typeKey] || { receivable: 0, received: 0, usd_receivable: 0, usd_received: 0 };
      const numVal = parseFloat(value) || 0;
      const updated = { ...current, [field]: numVal };
      if (field === "received") updated.received = Math.min(numVal, current.receivable || 0);
      if (field === "usd_received") updated.usd_received = Math.min(numVal, current.usd_receivable || 0);
      return { ...prev, [typeKey]: updated };
    });
  };

  const clientDisplayName = (c: ClientInfo) => c.is_single === false && c.partner_name ? `${c.name || "未命名"} & ${c.partner_name}` : c.name || "未命名";

  const currentStage = stages.find((s) => s.id === selectedStage);

  return (
    <div className="space-y-5">
      <div><h1 className="text-xl font-bold text-[#1a3a5c]">流程进度管理</h1><p className="text-xs text-gray-400 mt-1">管理客户试管助孕流程进度，上传影像与文档</p></div>

      {toastVisible && message && (
        <div
          className={`fixed inset-0 z-[200] flex items-center justify-center pointer-events-none transition-opacity duration-400 ${
            toastFading ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className={`flex items-center gap-2.5 px-6 py-3.5 rounded-2xl shadow-xl text-sm font-medium max-w-xs text-center ${
            message.includes("成功") || message.includes("保存") || message.includes("自动") || message.includes("已确认")
              ? "bg-emerald-600 text-white"
              : "bg-[#1a3a5c] text-white"
          }`}>
            <i className={`${message.includes("成功") || message.includes("保存") || message.includes("自动") || message.includes("已确认") ? "ri-checkbox-circle-line" : "ri-information-line"} text-base shrink-0`}></i>
            <span>{message}</span>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <label className="block text-xs font-medium text-gray-500 mb-2">选择客户</label>
        <select value={selectedClient || ""} onChange={(e) => { const val = Number(e.target.value) || null; setSelectedClient(val); setSelectedStage(null); if (val) setSearchParams({ client_id: String(val) }); else setSearchParams({}); }} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
          <option value="">请选择客户</option>
          {clients.map((c) => (<option key={c.id} value={c.id}>{clientDisplayName(c)} · {(c as any).client_code || c.phone}</option>))}
        </select>
      </div>
      {selectedClient && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <StageList stages={stages} progress={progress} selectedStage={selectedStage} clientServicePlan={clientServicePlan} clientJointStim={clientJointStim} loading={loading} hasExpensePerm={hasExpensePerm} onSelectStage={setSelectedStage} />
          <div className="lg:col-span-2 space-y-4">
            {selectedStage ? (
              <StageEditor stage={currentStage} progress={progress} selectedStage={selectedStage} selectedClient={selectedClient} status={status} notes={notes} feeReceivable={feeReceivable} feeReceived={feeReceived} feeUsdReceivable={feeUsdReceivable} feeUsdReceived={feeUsdReceived} media={media} documents={documents} uploading={uploading} mediaUploadProgress={mediaUploadProgress} docUploadProgress={docUploadProgress} saving={saving} onStatusChange={setStatus} onNotesChange={setNotes} onFeeReceivableChange={setFeeReceivable} onFeeReceivedChange={setFeeReceived} onFeeUsdReceivableChange={setFeeUsdReceivable} onFeeUsdReceivedChange={setFeeUsdReceived} onMediaUpload={handleUpload} onDocUpload={handleUpload} onDeleteMedia={removeMedia} onDeleteDoc={removeDoc} onReset={handleReset} onSave={handleSave} onShowNotify={() => setShowNotifyModal(true)} onPreviewFile={setPreviewFile} expenseData={expenseData} activeExpenseTypes={activeExpenseTypes} onToggleExpenseType={toggleExpenseType} onUpdateExpenseField={updateExpenseField} />
            ) : (<div className="bg-white rounded-xl shadow-sm p-8 text-center"><i className="ri-list-check-3 text-4xl text-gray-200 mb-3"></i><p className="text-sm text-gray-400">请选择左侧阶段进行编辑</p></div>)}
          </div>
        </div>
      )}

      <NotifyModal show={showNotifyModal} stageName={currentStage?.name} onClose={() => setShowNotifyModal(false)} onSend={handleSendNotification} />

      {previewFile && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewFile(null)}>
          <div className="relative max-w-[90vw] max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100"><p className="text-sm font-medium text-[#1a3a5c] truncate max-w-[70vw]">{previewFile.title}</p><div className="flex items-center gap-2"><button onClick={() => { const a = document.createElement("a"); a.href = previewFile.url; a.download = previewFile.title; a.click(); }} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer" title="下载"><i className="ri-download-line"></i></button><button onClick={() => setPreviewFile(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer"><i className="ri-close-line"></i></button></div></div>
            <div className="p-4 flex items-center justify-center bg-gray-900/5 min-h-[200px] max-h-[75vh] overflow-auto">{previewFile.type === "image" ? (<img src={resolveUploadUrl(previewFile.url)} alt={previewFile.title} className="max-w-full max-h-[70vh] object-contain rounded-lg" onError={(e) => { (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%239ca3af' font-size='12'%3E加载失败%3C/text%3E%3C/svg%3E"; }} />) : previewFile.type === "video" ? (<video src={resolveUploadUrl(previewFile.url)} controls className="max-w-full max-h-[70vh] rounded-lg" autoPlay>您的浏览器不支持视频播放</video>) : (<div className="text-center py-8 px-4"><i className="ri-file-text-line text-5xl text-gray-300 mb-3 block"></i><p className="text-sm text-gray-500 mb-2">{previewFile.title}</p><p className="text-xs text-gray-400 mb-4">{previewFile.type === "pdf" ? "PDF 文档" : "文档文件"}</p><button onClick={() => { const a = document.createElement("a"); a.href = resolveUploadUrl(previewFile.url); a.download = previewFile.title; a.click(); }} className="inline-flex items-center gap-1 px-4 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap"><i className="ri-download-line"></i>下载文件</button></div>)}</div>
          </div>
        </div>
      )}
    </div>
  );
}